<template>
  <app-modal
    v-model="isDialogOpened"
    :size="dialogSize"
    :no-click-animation="saveEnvelopeAsTemplatePending"
    :cancel-disabled="saveEnvelopeAsTemplatePending"
    @cancel="closeDialog"
  >
    <template #title>
      {{$t('project.signing-checklist.envelope-templates.dialogs.SaveDocumentAsTemplateDialog.title')}}
    </template>
    <template #body>
      <v-window v-model="currentStep">
        <v-window-item :value="1">
          <v-row>
            <v-col>
              <AppTextField :model-value="v$.templateData.title.$model"
                            :label="$t('project.signing-checklist.envelope-templates.dialogs.SaveDocumentAsTemplateDialog.templateTitleLabel')"
                            :error-messages="templateTitleErrors"
                            class="mt-2"
                            @update:model-value="handleInputChange($event)"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <Alert type="info"
                      color="light-blue"
                      :dismissible="false"
                      icon="info"
              >
                <p>{{ $t('project.signing-checklist.envelope-templates.dialogs.SaveDocumentAsTemplateDialog.alertInfo.presevedElements') }}</p>
                <ul>
                  <li>{{$t('project.signing-checklist.envelope-templates.dialogs.SaveDocumentAsTemplateDialog.alertInfo.signatureType')}}</li>
                  <li>{{$t('project.signing-checklist.envelope-templates.dialogs.SaveDocumentAsTemplateDialog.alertInfo.envelopeType')}}</li>
                  <li>{{$t('project.signing-checklist.envelope-templates.dialogs.SaveDocumentAsTemplateDialog.alertInfo.signers')}}</li>
                  <li>{{$t('project.signing-checklist.envelope-templates.dialogs.SaveDocumentAsTemplateDialog.alertInfo.tags')}}</li>
                </ul>
              </Alert>
            </v-col>
          </v-row>
        </v-window-item>

        <v-window-item :value="2">
          <SigningChecklistSectionDocumentSharingOptions :share-type="templateData.shareType"
                                                          :file-with-users="templateData.withUsers"
                                                          :file-rights="envelope.rights || { isCustom : true }"
                                                          :is-editing-limited="false"
                                                          @send-notif-change="setTemplateDocumentShareSendNotif"
                                                          @share-type-change="setTemplateDocumentShareType"
                                                          @with-users-change="setTemplateDocumentShareWithUsers"
          />
        </v-window-item>
      </v-window>
    </template>
    <template #footer>
      <AppButton v-if="currentStep === 2"
                 type="outlined"
                 variant="neutral"
                 :disabled="saveEnvelopeAsTemplatePending"
                 @click="currentStep--"
      >
        {{ $t('common.previous') }}
      </AppButton>
      <AppButton type="outlined"
                 variant="neutral"
                 class="ml-auto mr-2"
                 :disabled="saveEnvelopeAsTemplatePending"
                 @click="closeDialog"
      >
        {{ $t('common.cancel') }}
      </AppButton>
      <AppButton type="primary"
                  :disabled="!canValidateCurrentStep"
                  @click="onOk"
      >
        {{ okText }}
      </AppButton>
    </template>
  </app-modal>
</template>

<script>
import { useVuelidate } from '@vuelidate/core'
import { required, requiredIf } from '@vuelidate/validators'
import { reactive } from 'vue';
import { mapGetters, mapState } from 'vuex'

import Alert from '@/common/alerts/Alert.vue'
import AppTextField from '@/common/AppTextField.vue'
import { SHARE_TYPE_OPTIONS } from '@/common/utils/shareTypes'
import { typedMapActions } from '@/common/utils/store'
import { toast } from '@/common/utils/toast'
import AppButton from '@/design-system/buttons/AppButton.vue'
import { dialogMixin } from '@/mixins/dialogMixin'
import SigningChecklistSectionDocumentSharingOptions from '@/project/signing-checklist/add/SigningChecklistSectionDocumentSharingOptions.vue'
export default {
  name: 'SaveDocumentAsTemplateDialog',
  components: { Alert, AppButton, AppTextField, SigningChecklistSectionDocumentSharingOptions },
  mixins: [dialogMixin],
  props: {
    envelope: {
      type: Object,
      required: true,
    },
  },
  setup() {
    const templateData = reactive({
      title: '',
      shareType: SHARE_TYPE_OPTIONS.PUBLIC,
      withUsers: [],
      sendNotif: false,
    });
    const v$ = useVuelidate({
      templateData: {
        title: { required },
        shareType: { required },
        withUsers: { required: requiredIf((templateModel) => templateModel.shareType === SHARE_TYPE_OPTIONS.CUSTOM) },
      },
    }, { templateData });
    return {
      templateData,
      v$,
    };
  },
  data () {
    return {
      currentStep: 1,
    }
  },
  computed: {
    ...mapGetters('room', ['roomMnemo']),
    ...mapState('envelopeTemplates', ['saveEnvelopeAsTemplatePending']),
    okText () {
      return this.currentStep === 1 ? this.$t('common.next') : this.$t('common.register')
    },
    templateTitleErrors () {
      const errors = []
      if (!this.v$.templateData.title.$dirty) return errors
      if (this.v$.templateData.title.$invalid) {
        errors.push(this.$t('common.validations.fieldRequired', { fieldName: this.$t('project.signing-checklist.envelope-templates.dialogs.SaveDocumentAsTemplateDialog.templateTitleLabel') }))
      }
      return errors
    },
    canValidateCurrentStep () {
      const currentStepIsValid = this.currentStep === 1 || (this.currentStep === 2 && !this.v$.templateData.$invalid)

      return !this.saveEnvelopeAsTemplatePending && currentStepIsValid
    },
    dialogSize () {
      return this.currentStep === 2 ? 'lg' : 'md'
    },
  },
  methods: {
    ...typedMapActions('envelopeTemplates', ['SAVE_ENVELOPE_AS_TEMPLATE']),
    setTemplateDocumentShareSendNotif (sendNotif) {
      this.templateData.sendNotif = sendNotif
    },
    setTemplateDocumentShareType (shareType) {
      this.templateData.shareType = shareType
      this.templateData.withUsers = []
    },
    setTemplateDocumentShareWithUsers (withUsers) {
      this.templateData.withUsers = withUsers
    },
    onOk () {
      this.v$.$touch()
      if (this.currentStep === 2) {
        this.saveEnvelopeAsTemplate()
      } else {
        if (this.v$.templateData.title.$invalid) {
        return
      }
        this.currentStep++
      }
    },
    async saveEnvelopeAsTemplate () {
      try {
        await this.SAVE_ENVELOPE_AS_TEMPLATE({
          mnemo: this.roomMnemo,
          envelopeId: this.envelope.id,
          data: this.templateData,
        })
        toast.success(this.$t('project.signing-checklist.envelope-templates.dialogs.SaveDocumentAsTemplateDialog.saveEnvelopeAsTemplateSuccess'))
      } catch (error) {
        toast.error(this.$t('project.signing-checklist.envelope-templates.dialogs.SaveDocumentAsTemplateDialog.saveEnvelopeAsTemplateError'))
      } finally {
        this.closeDialog()
      }
    },
    handleInputChange (value) {
      this.templateData.title = value
    },
  },
}
</script>

<style lang="scss" scoped>
  .v-stepper__content {
    padding: 0 8px;
  }
</style>
